import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import { useAuth } from '../context/AuthContext';
import PhotoUpload from '../services/PhotoUpload'; // 匯入 PhotoUpload 元件
import FaceRegistration from './FaceRegistration'; // 匯入人臉註冊元件
import { QRCodeSVG } from 'qrcode.react'; // 匯入 QR Code 元件
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  CardActions,
  Chip,
  Alert
} from '@mui/material';
import {
  Face,
  PersonAdd,
  Delete,
  CheckCircle,
  Error,
  QrCode2
} from '@mui/icons-material';
import Lightbox from 'react-image-lightbox'; // 匯入 Lightbox
import 'react-image-lightbox/style.css'; // 匯入 Lightbox 的樣式

function Personal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams(); // 獲取URL參數
  const [employee, setEmployee] = useState({});
  const [originalEmployee, setOriginalEmployee] = useState({}); // 儲存原始資料用於比較
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); // 錯誤狀態
  const [isLightboxOpen, setIsLightboxOpen] = useState(false); // 控制 Lightbox 開啟狀態
  const [lightboxImage, setLightboxImage] = useState(''); // Lightbox 顯示的圖片
  const [componentKey, setComponentKey] = useState(0); // 用於強制重新渲染組件，避免 DOM 錯誤
  // 人臉註冊相關狀態
  const [isFaceRegistrationOpen, setIsFaceRegistrationOpen] = useState(false);
  const [faceRegistrationStatus, setFaceRegistrationStatus] = useState(null);
  // EDM 分享連結相關狀態
  const [shareUrl, setShareUrl] = useState(null);
  const [isGeneratingShare, setIsGeneratingShare] = useState(false);
  // 使用URL參數的ID，如果沒有則使用當前用戶ID
  const e_id = id || user?.e_id;
  const token = localStorage.getItem('token');

  // 初始化時讀取社友資料和表格設定
  useEffect(() => {
    const fetchEmployeeData = async () => {
      try {
        // 驗證 e_id 是否有效
        if (!e_id) {
          console.error('無效的員工 ID');
          setLoading(false);
          return;
        }

        // 🔒 重要安全檢查：只允許用戶查看自己的資料
        // 如果URL中的ID與當前登入用戶的ID不符，則拒絕存取
        if (id && user?.e_id && parseInt(id) !== parseInt(user.e_id)) {
          console.error('權限拒絕：試圖存取其他用戶的個人資料');
          setError('您只能查看自己的個人資料');
          setLoading(false);
          return;
        }

        // 查詢社友資料
        const employeeResponse = await axios.get(`/employees/${e_id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setEmployee(employeeResponse.data);
        setOriginalEmployee(employeeResponse.data); // 儲存原始資料
        
        // 設定人臉註冊狀態
        setFaceRegistrationStatus({
          registered: employeeResponse.data.e_face_registered === 1,
          confidence: employeeResponse.data.e_face_confidence,
          registeredAt: employeeResponse.data.e_face_created_at
        });


        setLoading(false);
        setComponentKey(prev => prev + 1); // 強制重新渲染，避免 DOM 衝突
      } catch (error) {
        console.error('讀取資料失敗:', error);
        
        // 處理權限錯誤
        if (error.response?.status === 403) {
          setError(error.response.data.message || '您沒有權限查看此資料');
        } else if (error.response?.status === 401) {
          setError('登入已過期，請重新登入');
        } else {
          setError('無法載入個人資料，請稍後再試');
        }
        
        setLoading(false);
      }
    };

    fetchEmployeeData();
    
    // 清理函數
    return () => {
      setEmployee({});
      setOriginalEmployee({});
      setFaceRegistrationStatus(null);
      setIsLightboxOpen(false);
      setLightboxImage('');
    };
  }, [e_id, token, id, user?.e_id]);

  // 🏗️ 直接定義可編輯的欄位配置 (移除對 sqltableorder 的依賴)
  const editableFields = [
    // 🔒 不可編輯的基本資訊
    
    { field: 'store_name', label: '社別', editable: false }, // 🔒 社別不可編輯
    { field: 'position_title', label: '社團職稱', editable: false }, // 🔒 社團職稱不可編輯
    { field: 'e_name', label: '姓名', editable: false }, // 🔒 姓名不可編輯
    //{ field: 'e_birthday', label: '生日', editable: false }, // 🔒 生日不可編輯
    { field: 'e_mobile', label: '手機', editable: false }, // 🔒 手機不可編輯
    //{ field: 'e_UID', label: '身分證字號', editable: false }, // 🔒 身分證字號不可編輯
    
    // 🔒 不可編輯的組別資訊
    { field: 'e_set', label: '組別', editable: false }, // 🔒 組別不可編輯
    { field: 'e_set_position', label: '組別職稱', editable: false }, // 🔒 組別職稱不可編輯
    
    // 可編輯的基本資訊
    { field: 'e_tel', label: '住家電話', editable: true },
    { field: 'e_email', label: '電子郵件', editable: true },
    
    // 通訊地址
    { field: 'e_zip2', label: '通訊郵遞區號', editable: true },
    { field: 'e_address2', label: '通訊地址', editable: true },
    
    // 公司資訊
    { field: 'e_comp_name', label: '公司名稱', editable: true },
    { field: 'e_comp_position', label: '公司職位', editable: true },
    { field: 'e_zip1', label: '公司郵遞區號', editable: true },
    { field: 'e_address1', label: '公司地址', editable: true },
    { field: 'e_tel1', label: '公司電話', editable: true },
    { field: 'e_fax', label: '傳真', editable: true },
    { field: 'e_comp_web', label: '公司網站', editable: true },
    
    // 其他資訊
    { field: 'sql_title1', label: '籍貫', editable: true },
    { field: 'sql_title2', label: '學歷', editable: true },
    { field: 'sql_title3', label: '年會介紹詞', editable: true },
    { field: 'sql_title4', label: '營業項目', editable: true },
    { field: 'sql_title5', label: '社團經歷', editable: true },
    { field: 'sql_title6', label: 'GOLF差點', editable: true },
    // { field: 'e_sell_info', label: '銷售資訊', editable: true }, // 🚫 隱藏銷售資訊欄位
    { field: 'e_slogan', label: '社長年度主題', editable: true },
    { field: 'e_size', label: '衣服尺寸', editable: true },
    { field: 'e_remark', label: '備註', editable: true },
  ];

  // 處理表單變更
  const handleChange = (e) => {
    const { name, value } = e.target;
    setEmployee((prevEmployee) => ({ ...prevEmployee, [name]: value }));
  };

  // 特殊處理 e_foodType 欄位的值變更
  const handleFoodTypeChange = (e) => {
    const value = e.target.value === '葷食' ? 1 : 2;
    setEmployee((prevEmployee) => ({ ...prevEmployee, e_foodType: value }));
  };

  // 更新座標的函數
  const updateCoordinates = async (e_id) => {
    try {
      await axios.post('/update-geocode', 
        { e_id },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      console.log('座標更新成功');
    } catch (error) {
      console.error('座標更新失敗:', error);
    }
  };

  // 提交表單更新資料
  const handleSubmit = async (e) => {
  e.preventDefault();

  try {
    // 準備更新請求的資料
    const payload = { ...employee };

    // 發送更新請求
    await axios.put(`/employees/${e_id}`, payload, {
      headers: { Authorization: `Bearer ${token}` }
    });

    // 檢查公司地址是否有變更
    if (originalEmployee.e_address1 !== employee.e_address1 && employee.e_address1) {
      console.log('公司地址已變更，開始更新座標...');
      await updateCoordinates(e_id);
    }

    alert('資料更新成功');
    // 重新載入員工資料以顯示最新更新
    const employeeResponse = await axios.get(`/employees/${e_id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setEmployee(employeeResponse.data);
    setOriginalEmployee(employeeResponse.data);
  } catch (error) {
    console.error('更新失敗:', error);

    // 錯誤處理
    if (error.response?.status === 403) {
      alert(error.response.data.message || '您沒有權限更新此資料');
    } else if (error.response?.status === 401) {
      alert('登入已過期，請重新登入');
      navigate('/login');
    } else {
      alert('更新失敗，請重試');
    }
  }
};


  // 日期格式化函數：將 ISO 字串轉為 MySQL 格式
  const formatDateForMySQL = (date = new Date()) => {
    return date.toISOString().substring(0,19).replace('T',' ');
  };

  // 人臉註冊成功回調
  const handleFaceRegistrationSuccess = (data) => {
    console.log('人臉註冊成功:', data);
    
    // 更新狀態
    setFaceRegistrationStatus({
      registered: true,
      confidence: data.confidence,
      registeredAt: formatDateForMySQL()
    });
    
    // 更新 employee 資料 (時間戳由後端 face API 自動設置)
    setEmployee(prev => ({
      ...prev,
      e_face_registered: 1,
      e_face_confidence: data.confidence,
      e_idcard: data.modelFile // 將模型檔名存入 e_idcard
    }));
    
    setIsFaceRegistrationOpen(false);
    setComponentKey(prev => prev + 1); // 強制重新渲染，避免 DOM 衝突
    alert('人臉註冊成功！您現在可以使用人臉識別簽到了。');
  };

  // 刪除人臉資料
  const handleDeleteFaceData = async () => {
    if (!window.confirm('確定要刪除人臉資料嗎？刪除後將無法使用人臉簽到功能。')) {
      return;
    }
    
    try {
      const response = await axios.delete(`/face/delete-model/${e_id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.data.success) {
        setFaceRegistrationStatus({
          registered: false,
          confidence: null,
          registeredAt: null
        });
        
        setEmployee(prev => ({
          ...prev,
          e_face_registered: 0,
          e_face_confidence: null,
          e_idcard: null
        }));
        
        setComponentKey(prev => prev + 1); // 強制重新渲染，避免 DOM 衝突
        alert('人臉資料已成功刪除');
      }
    } catch (error) {
      console.error('刪除人臉資料失敗:', error);
      alert('刪除失敗，請重試');
    }
  };

  // 生成 EDM 分享連結
  const generateShareLink = async () => {
    setIsGeneratingShare(true);
    try {
      const response = await axios.get(`/public/generate-share-token/${e_id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        setShareUrl(response.data.shareUrl);
        alert('分享連結已生成！');
      }
    } catch (error) {
      console.error('生成分享連結失敗:', error);
      alert('生成分享連結失敗，請稍後再試');
    } finally {
      setIsGeneratingShare(false);
    }
  };

  // 複製分享連結到剪貼簿
  const copyShareLink = () => {
    if (!shareUrl) return;
    
    navigator.clipboard.writeText(shareUrl).then(() => {
      alert('分享連結已複製到剪貼簿！');
    }).catch(err => {
      console.error('複製失敗:', err);
      alert('複製失敗，請手動複製');
    });
  };

  // 驗證用戶權限和ID有效性
  if (!e_id) {
    return (
      <Container maxWidth="sm" style={{ backgroundColor: '#FFFFFF', color: '#000000', fontFamily: 'Arial, sans-serif' }}>
        <Typography variant="h4" align="center" gutterBottom color="error">
          錯誤：無法取得用戶資訊
        </Typography>
        <Typography variant="body1" align="center">
          請重新登入系統
        </Typography>
        <Box display="flex" justifyContent="center" mt={2}>
          <Button variant="contained" color="primary" onClick={() => navigate('/login')}>
            返回登入頁面
          </Button>
        </Box>
      </Container>
    );
  }

  // 顯示權限錯誤訊息
  if (error) {
    return (
      <Container maxWidth="sm" style={{ backgroundColor: '#FFFFFF', color: '#000000', fontFamily: 'Arial, sans-serif' }}>
        <Typography variant="h4" align="center" gutterBottom color="error">
          存取被拒絕
        </Typography>
        <Typography variant="body1" align="center" color="error">
          {error}
        </Typography>
        <Box display="flex" justifyContent="center" mt={2}>
          <Button variant="contained" color="primary" onClick={() => navigate('/Home')}>
            返回首頁
          </Button>
        </Box>
      </Container>
    );
  }

  if (loading) {
    return <div>資料載入中...</div>;
  }

  // 當照片上傳成功後更新指定的 employee 欄位
const handleUploadSuccess = (fileName, fieldName) => {
  setEmployee((prevEmployee) => ({ ...prevEmployee, [fieldName]: fileName }));
};

// 當照片移除成功後清空指定的 employee 欄位
const handleRemoveSuccess = (fieldName) => {
  setEmployee((prevEmployee) => ({ ...prevEmployee, [fieldName]: null }));
};


  const photoUrl = employee.e_photo
  ? `/uploads/member/${employee.e_photo}?t=${new Date().getTime()}`
  : '/uploads/member/default.jpg';

const EDMphotoUrl = employee.EDM_photo
  ? `/uploads/EDM/${employee.EDM_photo}?t=${new Date().getTime()}`
  : '/uploads/EDM/default.jpg';

  // 生成 vCard 格式的聯絡資訊
  const generateVCard = () => {
    // 姓名 (直接使用完整姓名，不拆分)
    const fullName = employee.e_name || '';
    
    // 組織名稱 (公司名稱在前，社別在後)
    const compName = employee.e_comp_name || '';
    const storeName = employee.store_name || employee.ss_name || '';
    const org = compName ? (storeName ? `${compName} / ${storeName}` : compName) : storeName;
    
    // 職位 (公司職位在前，社團職稱在後)
    const compPosition = employee.e_comp_position || '';
    const storePosition = employee.position_title || '';
    const title = compPosition ? (storePosition ? `${compPosition} / ${storePosition}` : compPosition) : storePosition;
    
    // 手機號碼 (確保格式正確)
    const mobile = employee.e_mobile ? `+886${employee.e_mobile.replace(/^0/, '')}` : '';
    
    // Email
    const email = employee.e_email || '';
    
    // 公司網站
    const url = employee.e_comp_web || '';
    
    // 工作地址 (公司地址)
    const workAddress = employee.e_address1 || '';
    
    // 公司電話
    const workTel = employee.e_tel1 || '';
    
    return `BEGIN:VCARD
VERSION:3.0
N:${fullName};;;;
FN:${fullName}
ORG:${org}
TITLE:${title}
TEL;CELL:${mobile}
TEL;WORK:${workTel}
EMAIL:${email}
URL:${url}
ADR;WORK:;;${workAddress};台灣
END:VCARD`;
  };

  // 下載 vCard 檔案函數
  const downloadVCard = () => {
    const vCardContent = generateVCard();
    const blob = new Blob([vCardContent], { type: 'text/vcard;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${employee.e_name || 'contact'}.vcf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // 內聯樣式：將背景顏色改為白色
const lightModeStyles = {
  backgroundColor: '#FFFFFF',  // 設為白色背景
  color: '#000000',            // 設為黑色文字
  fontFamily: 'Arial, sans-serif',
};

  return (
    <Container key={`personal-${componentKey}`} maxWidth="sm" style={lightModeStyles}>
      <Typography variant="h4" align="center" gutterBottom>
        個人資料管理
      </Typography>
      <Box component="form" onSubmit={handleSubmit} noValidate>
  {/* 社友相片上傳模組 */}
  <Box display="flex" justifyContent="center" mb={2}>
    <PhotoUpload
      e_id={e_id}
      token={token}
      fileCategory="member" // 檔案分類
      tableName="employees" // 資料表名稱
      columnName="e_photo" // 欄位名稱
      idColumn="e_id" // 主鍵欄位名稱
      currentFileName={employee.e_photo} // 當前檔案名稱
      buttonText="上傳個人相片" // 自訂按鈕文字
      onUploadSuccess={(fileName) => handleUploadSuccess(fileName, 'e_photo')}
      onRemoveSuccess={() => handleRemoveSuccess('e_photo')} // 移除成功回調
      onImageClick={(imageUrl) => {
        setLightboxImage(imageUrl);
        setIsLightboxOpen(true);
      }} // 點擊放大功能
    />
  </Box>

  {/* 個人聯絡資訊 QR Code */}
  {employee.e_name && (
    <Card sx={{ mb: 3, backgroundColor: '#f8f9fa' }}>
      <CardContent>
        <Box display="flex" alignItems="center" mb={2}>
          <QrCode2 sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">
            個人聯絡資訊 QR Code
          </Typography>
        </Box>
        
        <Box display="flex" flexDirection="column" alignItems="center">
          <Box 
            sx={{ 
              padding: 2, 
              backgroundColor: 'white', 
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}
          >
            <QRCodeSVG
              value={generateVCard()}
              size={200}
              level="H"
              includeMargin={true}
            />
          </Box>
          
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2, textAlign: 'center' }}>
            掃描此 QR Code 即可儲存我的聯絡資訊到通訊錄
          </Typography>
          
          <Alert severity="info" sx={{ mt: 2, width: '100%' }}>
            <Typography variant="body2">
              <strong>包含資訊：</strong><br />
              姓名、社別、職位、手機、Email
              {employee.e_comp_name && '、公司名稱'}
              {employee.e_address1 && '、公司地址'}
            </Typography>
          </Alert>
          
          {/* 下載 vCard 按鈕 */}
          <Button
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mt: 2 }}
            onClick={downloadVCard}
          >
            下載聯絡資訊 (.vcf)
          </Button>
        </Box>
      </CardContent>
    </Card>
  )}

        
        {/* 人臉識別設定區域 */}
        <Card sx={{ mt: 3, mb: 2 }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <Face sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">
                人臉識別設定
              </Typography>
              <Chip label="V15-高精度" size="small" color="error" sx={{ ml: 1, fontSize: '0.7rem' }} />
            </Box>
            
            {faceRegistrationStatus?.registered ? (
              <>
                <Box display="flex" alignItems="center" mb={2}>
                  <CheckCircle color="success" sx={{ mr: 1 }} />
                  <Typography variant="body1" color="success.main">
                    已註冊人臉識別
                  </Typography>
                  <Chip 
                    label={`信心度: ${(faceRegistrationStatus.confidence * 100).toFixed(1)}%`}
                    color="success"
                    size="small"
                    sx={{ ml: 2 }}
                  />
                </Box>
                
                {faceRegistrationStatus.registeredAt && (
                  <Typography variant="body2" color="textSecondary" mb={2}>
                    註冊時間: {new Date(faceRegistrationStatus.registeredAt).toLocaleString('zh-TW')}
                  </Typography>
                )}
                
                <Alert severity="info" sx={{ mb: 2 }}>
                  您已成功註冊人臉識別，現在可以在活動簽到時使用人臉識別功能。
                </Alert>
              </>
            ) : (
              <>
                <Box display="flex" alignItems="center" mb={2}>
                  <Error color="warning" sx={{ mr: 1 }} />
                  <Typography variant="body1" color="warning.main">
                    尚未註冊人臉識別
                  </Typography>
                </Box>
                
                <Alert severity="warning" sx={{ mb: 2 }}>
                  註冊人臉識別後，您就可以在活動簽到時使用更方便的人臉識別功能。
                </Alert>
              </>
            )}
          </CardContent>
          
          <CardActions>
            {faceRegistrationStatus?.registered ? (
              <>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<PersonAdd />}
                  onClick={() => setIsFaceRegistrationOpen(true)}
                >
                  重新註冊
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={handleDeleteFaceData}
                >
                  刪除人臉資料
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                color="primary"
                startIcon={<PersonAdd />}
                onClick={() => setIsFaceRegistrationOpen(true)}
              >
                開始人臉註冊
              </Button>
            )}
          </CardActions>
        </Card>
        {/* 🎯 使用直接定義的欄位配置 (取代 sqltableorder 動態查詢) */}
        {editableFields.map((fieldConfig) => {
          const { field, label, editable } = fieldConfig;
          return (
            <TextField
              key={field}
              label={label}
              fullWidth
              name={field}
              value={employee[field] || ''}
              onChange={handleChange}
              margin="normal"
              disabled={!editable}
              InputProps={{ 
                readOnly: !editable 
              }}
              helperText={!editable ? '此欄位不可修改' : ''}
            />
          );
        })}
        
        {/* 🍽️ 飲食習慣 - 放在衣服尺寸後 */}
        <FormControl fullWidth margin="normal">
          <InputLabel>飲食習慣</InputLabel>
          <Select
            value={employee.e_foodType === 1 ? '葷食' : '素食'}
            onChange={handleFoodTypeChange}
          >
            <MenuItem value="葷食">葷食</MenuItem>
            <MenuItem value="素食">素食</MenuItem>
          </Select>
        </FormControl>
        
        {/* EDM相片上傳模組 */}
        <Box display="flex" justifyContent="center" mt={4} mb={2}>
          <PhotoUpload
            e_id={e_id}
            token={token}
            fileCategory="EDM" // 檔案分類
            tableName="employees" // 資料表名稱
            columnName="EDM_photo" // 欄位名稱
            idColumn="e_id" // 主鍵欄位名稱
            currentFileName={employee.EDM_photo} // 當前檔案名稱
            buttonText="上傳公司簡介A4" // 自訂按鈕文字
            onUploadSuccess={(fileName) => handleUploadSuccess(fileName, 'EDM_photo')}
            onRemoveSuccess={() => handleRemoveSuccess('EDM_photo')} // 移除成功回調
            onImageClick={(imageUrl) => {
              setLightboxImage(imageUrl);
              setIsLightboxOpen(true);
            }} // 點擊放大功能
          />
        </Box>
        
        {/* EDM 公開分享連結區域 */}
        {employee.EDM_photo && (
          <Card sx={{ mt: 3, mb: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <QrCode2 sx={{ mr: 1, color: 'success.main' }} />
                <Typography variant="h6">
                  公開分享 EDM
                </Typography>
                <Chip label="不需登入" size="small" color="success" sx={{ ml: 1 }} />
              </Box>
              
              <Alert severity="info" sx={{ mb: 2 }}>
                生成公開分享連結後，任何人都可以透過此連結查看您的公司簡介 EDM，
                無需登入系統。適合分享給客戶或合作夥伴。
              </Alert>
              
              {shareUrl ? (
                <Box>
                  <TextField
                    fullWidth
                    value={shareUrl}
                    InputProps={{
                      readOnly: true,
                    }}
                    sx={{ mb: 2 }}
                  />
                  <Box display="flex" gap={2}>
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      onClick={copyShareLink}
                    >
                      複製分享連結
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => window.open(shareUrl, '_blank')}
                    >
                      預覽
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Button
                  variant="contained"
                  color="success"
                  fullWidth
                  onClick={generateShareLink}
                  disabled={isGeneratingShare}
                >
                  {isGeneratingShare ? '生成中...' : '生成分享連結'}
                </Button>
              )}
            </CardContent>
          </Card>
        )}
        
        <Box display="flex" justifyContent="space-between" mt={3}>
          <Button type="submit" variant="contained" color="primary">
            儲存變更
          </Button>
          <Button variant="contained" color="secondary" onClick={() => navigate('/Home')}>
            返回
          </Button>
        </Box>
      </Box>
      {/* Lightbox 顯示區域 - 設置更高的 z-index 以顯示在 Modal 前面 */}
      {isLightboxOpen && (
        <Lightbox
          mainSrc={lightboxImage}
          onCloseRequest={() => setIsLightboxOpen(false)}
          reactModalStyle={{
            overlay: { zIndex: 1060 },
            content: { zIndex: 1061 }
          }}
        />
      )}
      
      {/* 人臉註冊對話框 */}
      {isFaceRegistrationOpen && (
        <FaceRegistration
          key={`face-registration-${componentKey}`}
          open={isFaceRegistrationOpen}
          onClose={() => {
            setIsFaceRegistrationOpen(false);
            setComponentKey(prev => prev + 1); // 關閉時重新渲染，清理狀態
          }}
          employeeId={e_id}
          token={token}
          onSuccess={handleFaceRegistrationSuccess}
          isReregistration={faceRegistrationStatus?.registered}
        />
      )}
    </Container>
  );
}

export default Personal;
